
import {
  Form,
  Input,
  Checkbox,
  Button,
  FormItem,
  Select,
  Option,
  CollapseTransition,
  OptionGroup,
  Drawer,
} from 'element-ui';
import { ChevronDownIcon, XIcon } from 'vue-feather-icons';
import addressMixin from '@/mixins/addressMixin';

export default {
  props: {
    address: {
      type: Object,
      default: null,
    },
    cancelButton: {
      type: Boolean,
      default: true,
    },
    isDelivery: {
      type: Boolean,
      default: true,
    },
    isInvoice: {
      type: Boolean,
      default: true,
    },
    showSave: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [addressMixin],
  data() {
    return {
      customer: null,
      openAdd: false,
      showAddressLine2: false,
      addressInfo: this.getInitialAddressInfo(),
      clientCountry: null,
      toggleDrawer: true,
      direction: 'ltr',
      title: this.$t('Address'),
    };
  },
  computed: {
    countries() {
      return this.$localisation.countries;
    },
    getClientCountry() {
      return this.countries.find(
        (c) => c.CountryIsoCode === this.clientCountry?.country
      );
    },
    rules() {
      const required = (msg) => ({
        required: true,
        message: this.$t(msg || 'Required Field'),
        trigger: 'blur',
      });
      return {
        Lastname: [required()],
        Firstname: [required()],
        MobilePhone: [required()],
      };
    },
  },
  watch: {
    address: {
      handler() {
        this.loadEditAddress();
      },
      deep: true,
    },
    addressInfo: {
      handler(val) {
        this.$emit('onAddressFilled', val);
      },
      deep: true,
    },
    'addressInfo.CountryIsoCode': {
      handler(val) {
        if (!val) {
          this.addressInfo.Country = '';
          return;
        }
        const found = this.countries.find((c) => c.CountryIsoCode === val);
        if (found) {
          this.addressInfo.Country = found.CountryName;
        }
      },
      immediate: true,
    },
  },
  created() {
    this.syncCustomer();
  },
  async mounted() {
    await this.loadClientCountryIsoCode();
    this.loadEditAddress();

    // Global checkout triggers
    const bus = (ev, cb) => this.$root.$on(ev, cb);
    bus('checkout:validate', this.validateForCheckout);
    bus('checkout:clear-validate', () =>
      this.$refs.AddressForm?.clearValidate()
    );
  },
  beforeDestroy() {
    this.$root.$off('checkout:validate', this.validateForCheckout);
    this.$root.$off('checkout:clear-validate');
  },
  methods: {
    validateForCheckout() {
      this.$refs.AddressForm?.validate(() => {});
      this.$refs.AddressFormDrawer?.validateForCheckout?.();
    },
    getInitialAddressInfo() {
      // Sync names from current session if starting fresh
      const cust =
        this.$auth.user?.Customer || this.$store.state.user.guest.guestUser;
      return {
        Alias: '',
        Lastname: cust?.Lastname || '',
        Firstname: cust?.Firstname || '',
        Address1: '',
        Address2: '',
        Postcode: '',
        City: '',
        StateName: '',
        StateCode: '',
        StateIsoCode: '',
        CountryIsoCode: '',
        Country: '',
        MobilePhoneCode: '+33',
        MobilePhone: cust?.MobilePhone || '',
        PhoneCode: '+33',
        Phone: '',
        Company: '',
        IsInvoice: this.isInvoice,
        IsDelivery: this.isDelivery,
      };
    },
    loadEditAddress() {
      if (this.address) {
        Object.assign(this.addressInfo, this.address);
        if (this.address.Address2) this.showAddressLine2 = true;
      } else {
        this.addressInfo = this.getInitialAddressInfo();
        this.showAddressLine2 = false;
      }
    },
    onAddressTextChange(e) {
      this.addressInfo.Address1 = e.text;
    },
    onStateNameChange() {
      this.addressInfo.StateCode = '';
      this.addressInfo.StateIsoCode = '';
    },

    syncCustomer() {
      this.customer =
        this.$auth.user?.Customer || this.$store.state.user.guest.guestUser;
    },
    async loadClientCountryIsoCode() {
      try {
        const res = await this.$store.dispatch('shop/localisation/fetchVisitor');
        this.clientCountry = res;
        // Default to detected country if not set
        if (!this.addressInfo.CountryIsoCode && res?.country) {
          this.addressInfo.CountryIsoCode = res.country;
        }
      } catch (e) {}
    },
    saveAddress() {
      this.addressService.add(this.addressInfo).then((data) => {
        this.addressService.fetchAddresses({
          onSuccess: () => {
            this.$store.commit('checkout/setCheckoutError', { data: null });
            this.$root.$emit('checkout:clear-validate');
            this.$emit('onSubmit', data);
          },
        });
      });
    },
    updateAddress() {
      this.addressService
        .update(this.address.IdAddress, this.addressInfo)
        .then((data) => {
          this.addressService.fetchAddresses({
            onSuccess: () => {
              this.$store.commit('checkout/setCheckoutError', { data: null });
              this.$root.$emit('checkout:clear-validate');
              this.$emit('onSubmit', data);
            },
          });
        });
    },
    async submitForm() {
      const isFormValid = await new Promise((resolve) => {
        this.$refs.AddressForm.validate((valid) => resolve(valid));
      });
      if (!isFormValid) return;

      const isDrawerValid =
        await this.$refs.AddressFormDrawer?.validateForCheckout?.();
      if (isDrawerValid === false) return;

      if (!this.$auth.loggedIn) {
        // For guests, we don't save to API yet, just emit and close
        this.$emit('onSubmit', this.addressInfo);
        return;
      }

      this.address ? this.updateAddress() : this.saveAddress();
    },
    mapGoogleAddress(ev) {
      if (!ev) return;
      this.addressInfo.Address1 = ev.courtAddress;
      this.addressInfo.City = ev.city || '';
      this.addressInfo.Postcode = ev.postalCode || '';
      this.addressInfo.StateName = ev.StateName || '';
      this.addressInfo.StateCode = ev.StateCode || '';
      this.addressInfo.StateIsoCode = ev.StateIsoCode || '';

      if (ev.country) {
        const found = this.countries.find(
          (c) =>
            c.CountryName?.toLowerCase() === ev.country?.toLowerCase()
        );
        if (found) {
          this.addressInfo.CountryIsoCode = found.CountryIsoCode;
          this.addressInfo.Country = found.CountryName;
        }
      }
    },
    onSelectAddress(ev) {
      this.mapGoogleAddress(ev);
    },
  },
  components: {
    Form,
    Input,
    Checkbox,
    Button,
    FormItem,
    Select,
    Option,
    OptionGroup,
    CollapseTransition,
    ChevronDownIcon,
    XIcon,
    Drawer,
  },
};
